import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import {
  Avatar,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Switch,
  Typography,
} from '@mui/material';
import { Box, Stack } from '@mui/system';
import { DataGrid } from '@mui/x-data-grid';
import { post } from 'api';
import { url } from 'config/config';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import { useAuth } from 'providers/auth-provider/AuthJwtProvider';
import IconifyIcon from 'components/base/IconifyIcon';
import DataGridPagination from 'components/pagination/DataGridPagination';
import StyledTextField from 'components/styled/StyledTextField';

const User = () => {
  const { sessionUser } = useAuth();
  // const [editOpen, setEditOpen] = useState(null);
  // console.log(editOpen);
  const ViewButton = ({ row }) => {
    const navigate = useNavigate();

    const handleView = () => {
      navigate('/user-info', { state: row });
    };

    return (
      <IconButton onClick={handleView}>
        <RemoveRedEyeOutlinedIcon />
      </IconButton>
    );
  };
  const EditButton = ({ row }) => {
    const navigate = useNavigate();

    const handleView = () => {
      navigate('/user-edit', { state: row });
    };

    return (
      <IconButton onClick={handleView}>
        <ModeEditOutlineOutlinedIcon />
      </IconButton>
    );
  };
  const columns = useMemo(
    () => [
      {
        field: 'view',
        headerName: 'View',
        sortable: false,
        filterable: false,
        minWidth: 80,
        renderCell: (params) => (
          <div>
            <ViewButton row={params.row} />
            <EditButton row={params.row} />
          </div>
        ),
      },
      {
        field: 'name',
        headerName: 'Name',
        minWidth: 280,
        // flex: 1,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const { firstName, lastName, profileImage, empId } = params.row;

          return (
            <Stack
              sx={{
                gap: 1.5,
                alignItems: 'center',
              }}
            >
              <Avatar
                alt={firstName.toUpperCase()}
                src={profileImage ? `${url}${profileImage}` : ''}
                sx={{ width: 24, height: 24 }}
              />
              <div>
                <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                  {`${firstName} ${lastName}`}
                </Typography>
                <Typography variant="caption">{`${empId}`}</Typography>
              </div>
            </Stack>
          );
        },
      },
      {
        field: 'role',
        headerName: 'Designation',
        minWidth: 200,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          return (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                {params.row.role}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'reportingManager',
        headerName: 'Reporting Manager',
        minWidth: 250,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const { firstName, lastName, role } = params.row.reportingManager;

          return (
            <Stack
              sx={{
                gap: 1.5,
                alignItems: 'center',
              }}
            >
              <div>
                <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                  {`${firstName} ${lastName}`}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 300, fontSize: '13px' }}>
                  {role}
                </Typography>
              </div>
            </Stack>
          );
        },
      },
      {
        field: 'punchIn',
        headerName: 'Attendance',
        minWidth: 150,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const { attendance } = params.row;

          return (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                {attendance && attendance.loginTime ? (
                  `${attendance?.loginTime ? moment(attendance?.loginTime).format('HH:mm A') : '-'}/${attendance?.logoutTime ? moment(attendance?.logoutTime).format('HH:mm A') : '-'}`
                ) : (
                  <Chip
                    label={attendance ? attendance?.status : 'Absent'}
                    color={
                      attendance && attendance?.status
                        ? attendance?.status == 'In Progress'
                          ? 'warning'
                          : 'primary'
                        : 'error'
                    }
                  />
                )}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'active',
        headerName: 'Active',
        minWidth: 140,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Switch
            disabled={sessionUser?._id == params?.row?._id}
            checked={Boolean(params.row.isActive)} // ✅ always controlled
            onClick={(e) => {
              e.stopPropagation(); // ✅ stop row selection
              setSelectedUser(params.row); // ✅ store clicked user
              setConfirmOpen(true); // ✅ open confirm dialog ONLY
            }}
            color="success"
          />
        ),
      },

      // {
      //   field: 'action',
      //   headerName: 'Action',
      //   align: 'right',
      //   width: 60,
      //   headerAlign: 'right',
      //   renderCell: () => <DashboardMenu />,
      // },
    ],
    [],
  );
  const [search, setSearch] = useState('');
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
    search: search,
  });
  const [candidate, setCandidate] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const toggleUserStatus = async (userId) => {
    try {
      const res = await post(`users/user_status_change`, { userId });

      enqueueSnackbar(res.message || 'User status updated successfully', {
        variant: 'success',
        autoHideDuration: 3000,
      });
      fetchData(); // refresh table
    } catch (err) {
      enqueueSnackbar(err.message || 'Failed to update user status', {
        variant: 'error',
        autoHideDuration: 3000,
      });
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPaginationModel((prev) => ({ ...prev, search: search }));
    }, 600);
    return () => clearTimeout(timeout);
  }, [search]);
  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await post('users/list_user', {
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        search: paginationModel.search,
      });
      if (res.status == 200) {
        setCandidate(res.data.data);
        setTotal(res.data.total);
      } else {
        enqueueSnackbar(res.message || 'Error', { variant: 'error', autoHideDuration: 3000 });
        setCandidate([]);
        setTotal(0);
      }
    } catch (error) {
      enqueueSnackbar(error.message || 'Error', { variant: 'error', autoHideDuration: 3000 });
      setCandidate([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [paginationModel]);

  return (
    <>
      <Paper sx={{ flex: 1, p: { xs: 3, md: 3 } }}>
        <StyledTextField
          id="search-box"
          type="search"
          fullWidth
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search User Name"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <IconifyIcon icon="material-symbols:search-rounded" fontSize={20} />
                </InputAdornment>
              ),
            },
          }}
          sx={{
            maxWidth: { sm: 200, md: 240 },
            flexGrow: { xs: 1, sm: 0 },
            mb: 3,
          }}
        />
        <Box sx={{ width: '100%' }}>
          <DataGrid
            loading={loading}
            rows={candidate}
            columns={columns}
            getRowId={(row) => row._id}
            pagination
            paginationMode="server"
            rowCount={total}
            paginationModel={paginationModel} // { page: 0, pageSize: 10 }
            onPaginationModelChange={(e) =>
              setPaginationModel(() => ({
                page: e.page,
                pageSize: e.pageSize,
                search,
              }))
            }
            pageSizeOptions={[5, 10, 20]}
            slots={{
              basePagination: (props) => <DataGridPagination showFullPagination {...props} />,
            }}
          />
        </Box>
      </Paper>
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Action</DialogTitle>

        <DialogContent>
          Are you sure you want to <b>{selectedUser?.isActive ? 'deactivate' : 'activate'}</b> this
          user?
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>

          <Button
            color="error"
            onClick={() => {
              toggleUserStatus(selectedUser._id, selectedUser.isActive);
              setConfirmOpen(false);
            }}
          >
            Yes, Confirm
          </Button>
        </DialogActions>
      </Dialog>
      {/* <EditUser editOpen={editOpen} /> */}
    </>
  );
};

export default User;
